import { useState, useEffect, useMemo, useRef } from "react";
import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { Label } from "@/modern/components/ui/label";
import { Input } from "@/modern/components/ui/input";
import { Switch } from "@/modern/components/ui/switch";
import { Card } from "@/modern/components/ui/card";
import { Combobox, TComboOption, TComboSelectedValue } from "@/modern/components/ui/combobox";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { KeyValueModel } from "@/bryntum-lib/models/KeyValueModel";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { TAGS_KEYS } from "@/other/constants/TagsKeys";
import { Info } from "lucide-react";

const MAX_ARCHIVE_DAYS = 9999;
const MIN_ARCHIVE_DAYS = 1;
const DEFAULT_ARCHIVE_DAYS = 365;
const ERROR_CLEAR_TIMEOUT = 1000;

const TaskAutoArchiveDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project, userPreferences, selectedBoard } = useSwivuBryntumContext();

  const [enableAutoArchive, setEnableAutoArchive] = useState(false);
  const [archiveDays, setArchiveDays] = useState(DEFAULT_ARCHIVE_DAYS);
  const [statusFilter, setStatusFilter] = useState<number[]>([]);
  const [inputError, setInputError] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const errorTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Subscribe to board preferences changes
  useSwivuStore<KeyValueModel>({
    storeId: STORE_KEYS.boardPreferencesStore,
    subscriberFn: ({ record }) => {
      const archiveKeys = [
        PERSISTENCE_STATE_KEYS.taskAutoArchiveEnabled,
        PERSISTENCE_STATE_KEYS.taskAutoArchiveDays,
        PERSISTENCE_STATE_KEYS.taskAutoArchiveStatusFilter
      ];

      if (record?.key && archiveKeys.includes(record.key)) {
        loadSettings();
      }
    }
  });

  // Get status options from taskTagValuesStore
  const statusOptions: TComboOption[] = useMemo(() => {
    if (!project) return [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const taskTagValuesStore = project.getCrudStore(STORE_KEYS.taskTagValuesStore) as any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const statusRecords = taskTagValuesStore.query(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (record: any) => record.tagSet === TAGS_KEYS.status,
      true
    ) || [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return statusRecords.map((record: any) => ({
      value: record.id,
      label: record.tagValue || ""
    }));
  }, [project]);

  useEffect(() => {
    loadSettings();
    checkPermissions();

    // Cleanup timeout on unmount
    return () => {
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
    };
  }, [userPreferences, selectedBoard]);

  const loadSettings = () => {
    if (!userPreferences) return;

    // Load enable state
    const isEnabled = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.taskAutoArchiveEnabled, "false", true);
    setEnableAutoArchive(isEnabled === "true");

    // Load archive days - handle both string and number values
    const days = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.taskAutoArchiveDays, String(DEFAULT_ARCHIVE_DAYS), true);
    const parsedDays = typeof days === "string" ? parseInt(days, 10) : days;
    setArchiveDays(!isNaN(parsedDays as number) ? (parsedDays as number) : DEFAULT_ARCHIVE_DAYS);

    // Load status filter - safely parse JSON
    const statusFilterSetting = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.taskAutoArchiveStatusFilter, "", true);
    if (statusFilterSetting && statusFilterSetting !== "null" && statusFilterSetting !== "") {
      try {
        const parsedFilter = typeof statusFilterSetting === "string"
          ? JSON.parse(statusFilterSetting)
          : statusFilterSetting;
        setStatusFilter(Array.isArray(parsedFilter) ? parsedFilter : []);
      } catch {
        setStatusFilter([]);
      }
    } else {
      setStatusFilter([]);
    }
  };

  const checkPermissions = () => {
    // @ts-expect-error - selectedBoard.record is dynamically typed
    const isOwner = selectedBoard?.record?.isOwner;
    setCanEdit(!!isOwner);
  };

  const handleEnableChange = (checked: boolean) => {
    if (!userPreferences || !canEdit) return;

    setEnableAutoArchive(checked);
    userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.taskAutoArchiveEnabled, String(checked), true);
  };

  const handleArchiveDaysInput = (e: React.FormEvent<HTMLInputElement>) => {
    if (!userPreferences || !canEdit) return;

    const value = e.currentTarget.value;
    const numValue = parseInt(value, 10);
    const originalValue = archiveDays;

    // Handle empty input
    if (value === "" || isNaN(numValue)) {
      return;
    }

    // Special handling for values exceeding max - divide by 10 like the legacy implementation
    if (numValue > MAX_ARCHIVE_DAYS) {
      const adjustedValue = Math.floor(originalValue / 10);
      setArchiveDays(adjustedValue);
      setInputError(`Archive days cannot be greater than ${MAX_ARCHIVE_DAYS}`);

      // Clear error timer if exists
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }

      // Auto-clear error after timeout
      errorTimerRef.current = setTimeout(() => {
        setInputError(null);
      }, ERROR_CLEAR_TIMEOUT);

      // Only persist if value actually changed during validation
      if (adjustedValue !== originalValue) {
        userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.taskAutoArchiveDays, String(adjustedValue), true);
      }
      return;
    }

    // Update state
    setArchiveDays(numValue);
  };

  const handleArchiveDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!userPreferences || !canEdit) return;

    const value = e.target.value;
    const numValue = parseInt(value, 10);

    // Handle empty or invalid input
    if (value === "" || isNaN(numValue)) {
      setInputError("Please enter a valid number");
      return;
    }

    // Enforce minimum value
    if (numValue < MIN_ARCHIVE_DAYS) {
      setArchiveDays(MIN_ARCHIVE_DAYS);
      userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.taskAutoArchiveDays, String(MIN_ARCHIVE_DAYS), true);
      setInputError(null);
      return;
    }

    // Enforce maximum value
    if (numValue > MAX_ARCHIVE_DAYS) {
      setInputError(`Archive days cannot be greater than ${MAX_ARCHIVE_DAYS}`);
      return;
    }

    // Valid value - persist it
    setArchiveDays(numValue);
    userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.taskAutoArchiveDays, String(numValue), true);
    setInputError(null);
  };

  const handleArchiveDaysFocus = () => {
    // Clear error on focus
    setInputError(null);
    if (errorTimerRef.current) {
      clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
  };

  const handleStatusFilterChange = (value: TComboSelectedValue | null) => {
    if (!userPreferences || !canEdit) return;

    const filterArray = Array.isArray(value) ? value : [];
    setStatusFilter(filterArray as number[]);
    userPreferences.updateSetting(
      PERSISTENCE_STATE_KEYS.taskAutoArchiveStatusFilter,
      JSON.stringify(filterArray),
      true
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      <div className="space-y-5 max-w-3xl">
        {/* Auto Archive Settings Card */}
        <Card className="py-3 px-4">
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Auto Archive Configuration</h3>
              <p className="text-xs text-muted-foreground">
                Enable automatic archiving for tasks that have been inactive
              </p>
            </div>

            <div className="space-y-4">
              {/* Enable Auto Archive */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="enable-auto-archive">Enable Auto Archive</Label>
                  <p className="text-xs text-muted-foreground">
                    Automatically archive inactive tasks based on the criteria below
                  </p>
                </div>
                <Switch
                  id="enable-auto-archive"
                  checked={enableAutoArchive}
                  onCheckedChange={handleEnableChange}
                  disabled={!canEdit}
                />
              </div>

              {/* Archive Days */}
              <div className="space-y-2">
                <Label htmlFor="archive-days">Archive Inactive Tasks Days-Older Than</Label>
                <Input
                  variant="background"
                  inputSize="selectTrigger"
                  id="archive-days"
                  type="number"
                  min={MIN_ARCHIVE_DAYS}
                  max={MAX_ARCHIVE_DAYS}
                  step={1}
                  value={archiveDays}
                  onInput={handleArchiveDaysInput}
                  onChange={handleArchiveDaysChange}
                  onFocus={handleArchiveDaysFocus}
                  disabled={!enableAutoArchive || !canEdit}
                  className={inputError ? "border-destructive" : ""}
                />
                {inputError && (
                  <p className="text-xs text-destructive">{inputError}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Number of days a task must be inactive before being archived ({MIN_ARCHIVE_DAYS}-{MAX_ARCHIVE_DAYS})
                </p>
              </div>

              {/* Status Filter */}
              <div className="space-y-2">
                <Label htmlFor="status-filter">Task Status Filter</Label>
                <Combobox
                  options={statusOptions}
                  value={statusFilter}
                  onValueChange={handleStatusFilterChange}
                  placeholder="None - All statuses will be considered"
                  searchPlaceholder="Search statuses..."
                  emptyMessage="No statuses found"
                  multiSelect={true}
                  disabled={!enableAutoArchive || !canEdit}
                  size="md"
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Optional: Select specific statuses to filter which tasks are archived. If none selected, all statuses will be considered.
                </p>
              </div>
            </div>

            {/* Permission Notice */}
            {!canEdit && (
              <div className="mt-4 p-3 bg-primary/5 rounded-lg border border-primary/20">
                <p className="text-xs text-foreground leading-relaxed">
                  <i className="fa-regular fa-info-circle mr-1" />
                  <strong>Note:</strong> You don&apos;t have permission to modify these settings.
                  Only board owners can configure auto-archive settings.
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Information Card */}
        <Card className="py-3 px-4 border-primary/20 bg-primary/5">
          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <Info className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-2">
                    What Constitutes An &quot;Inactive&quot; Task?
                  </h4>
                  <p className="text-sm text-foreground leading-relaxed">
                    A task is considered inactive when it has not been modified for the specified number of days.
                    This includes any changes to task properties such as name, description, dates, status, assignments,
                    notes, or any other task-related updates. Tasks with no recent activity are candidates for archiving
                    to help keep your active task list focused and manageable.
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-2">
                    How Auto Archive Works:
                  </h4>
                  <ul className="text-sm text-foreground space-y-2 list-none">
                    <li className="flex gap-2">
                      <span className="flex-shrink-0">•</span>
                      <span><strong>Daily Processing:</strong> Tasks are automatically archived once per day during system maintenance</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="flex-shrink-0">•</span>
                      <span><strong>First Run Notice:</strong> On initial activation, if there are many tasks to archive, this process may take some time</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="flex-shrink-0">•</span>
                      <span><strong>Status Filter:</strong> If specific statuses are selected, only tasks with those statuses will be considered for archiving</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="flex-shrink-0">•</span>
                      <span><strong>Selective Archiving:</strong> Only tasks meeting both the age criteria and optional status filter will be archived</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default TaskAutoArchiveDetail;
